import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate } from '../../../utils/dateUtils';
import { formatAmount } from '../../../utils/formatAmount';
import useRefundTransfer from './useRefundTransfer';
import useManageOrder from '../../order/useManageOrder';
import { useAuth } from '../../../context/AuthContext';
import RefundCreateModal from './RefundCreateModal';
import CaseTicketService from '../../caseTicket/CaseTicketService';
import ImageUploadModal from '../../../utils/ImageUploadModal';
import ImageModal from '../../weeklyShip/ImageModel';
import ConfirmDialog from '../../../sharedComponents/ConfirmDialog';
import { BANKS, REASONS, REASON_LABELS, BankBadge, BankLogoButton, PRODUCT_PAYMENT_STYLE, PAID_STATUSES } from './refundShared';

const STATUS_TABS = [
  { id: 'cancel',              label: 'รอดำเนินการ/ยกเลิก' },
  { id: 'pending_info',        label: 'รอข้อมูลบัญชี' },
  { id: 'ready_to_transfer',   label: 'รอโอน' },
  { id: 'transferred',         label: 'รอแจ้งสลิป ลค' },
  { id: 'customer_notified',   label: 'แจ้งโอนคืนแล้ว' },
  { id: 'report',              label: '📊 รายงาน' },
];

// Status -> human label, for the audit search results (a refund can be in any status).
const STATUS_LABELS = {
  pending_info:      'รอข้อมูลบัญชี',
  ready_to_transfer: 'รอโอน',
  transferred:       'รอแจ้งสลิป ลค',
  customer_notified: 'แจ้งโอนคืนแล้ว',
};

const RefundTransfer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.tab || new URLSearchParams(location.search).get('tab') || 'pending_info';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [reportFilter, setReportFilter] = useState({ startDate: '', endDate: '', reason: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingCancelOrders, setPendingCancelOrders] = useState(null);
  // Hand-off from a customer service case ticket (see features/caseTicket) — opens
  // this modal pre-filled instead of the usual cancel-tab flow.
  const [caseTicketPrefill, setCaseTicketPrefill] = useState(null);
  const caseTicketIdRef = useRef(null);
  const [informGroup, setInformGroup] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const [bankForms, setBankForms] = useState({});
  const [openBankForms, setOpenBankForms] = useState({});
  const [bankSaving, setBankSaving] = useState({});
  const [bankMode, setBankMode] = useState({});
  const [qrUploading, setQrUploading] = useState({});
  const [qrModalRecordId, setQrModalRecordId] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [receiptRecord, setReceiptRecord] = useState(null);
  // Delete (soft-delete) modal: target record + required reason.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteSaving, setDeleteSaving] = useState(false);
  // Amount-edit modal: target record + new amount (allowed before money moves).
  const [amountTarget, setAmountTarget] = useState(null);
  const [amountValue, setAmountValue] = useState('');
  const [amountSaving, setAmountSaving] = useState(false);
  // Report audit: search the whole refund flow (live + deleted) by an order URL.
  const [flowQuery, setFlowQuery] = useState('');
  const [flowResults, setFlowResults] = useState(null);
  const [flowSearching, setFlowSearching] = useState(false);

  const {
    records, isLoading, fetchRecords, saveBankInfo, uploadBankQr,
    markCustomerNotified, revertToPending, claimRefund, releaseRefund, fetchReport,
    deleteRecord, updateAmount, convertToCredit, searchFlow,
  } = useRefundTransfer();
  const { orderGroup, getCancelledOrders, processCancellation, resolveVoidCheck, claimCancelCase, releaseCancelCase } = useManageOrder();
  const { authState } = useAuth();
  // The credit-card void ACTIONS (void ได้/ไม่ได้) are superuser-only; other
  // admins see the same void_check items as a read-only info section instead.
  const isSuperuser = authState?.user?.role === 'superuser';

  useEffect(() => { fetchRecords(); getCancelledOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Arrived here from a case ticket's "ไปหน้าโอนเงินคืน" button — open the create
  // modal straight away, pre-filled, instead of making the admin start from scratch.
  useEffect(() => {
    if (location.state?.openCaseTicketRefund && location.state?.prefill) {
      caseTicketIdRef.current = location.state.caseTicketId || null;
      setCaseTicketPrefill(location.state.prefill);
      setPendingCancelOrders(null);
      setIsModalOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'report') {
      fetchReport().then(setReportData);
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live refresh: another admin claiming/releasing/notifying, or a new slip arriving,
  // updates the list (and the "taken by" locks) on this screen too.
  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, { withCredentials: true });
    socket.on('case_claim_updated', ({ caseType } = {}) => {
      if (caseType === 'refund') fetchRecords();
      else if (caseType === 'cancel') getCancelledOrders();
    });
    socket.on('refund_slip_uploaded', () => fetchRecords());
    return () => socket.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Force-close: if the case I have open is taken over by another admin, the
  // refreshed data shows it claimed-but-not-mine (server-computed) — close my modal.
  useEffect(() => {
    if (!receiptRecord) return;
    const fresh = (records || []).find((r) => r.id === receiptRecord.id);
    if (fresh && fresh.claimedByName && !fresh.claimedByMe) {
      setReceiptRecord(null);
      toast.info(`เคสนี้ถูก ${fresh.claimedByName} รับช่วงต่อแล้ว`);
    }
  }, [records]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const customerId = pendingCancelOrders?.[0]?.customerId ?? informGroup?.[0]?.customerId;
    if (!customerId) return;
    const myOrders = (orderGroup || []).filter((o) => o.customerId === customerId);
    if (!myOrders.length) return;
    const heldOrder = myOrders.find((o) => o.cancelClaimedByUserId);
    const mine = myOrders.some((o) => o.cancelClaimedByMe);
    if (heldOrder && !mine) {
      setPendingCancelOrders(null);
      setIsModalOpen(false);
      setInformGroup(null);
      toast.info(`เคสนี้ถูก ${heldOrder.cancelClaimedByName || 'แอดมินท่านอื่น'} รับช่วงต่อแล้ว`);
    }
  }, [orderGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRecords = records.filter((r) => r.status === activeTab);

  const tabCounts = {
    cancel:            (orderGroup || []).length,
    pending_info:      records.filter((r) => r.status === 'pending_info').length,
    ready_to_transfer: records.filter((r) => r.status === 'ready_to_transfer').length,
    transferred:       records.filter((r) => r.status === 'transferred').length,
    customer_notified: records.filter((r) => r.status === 'customer_notified').length,
  };

  const openModal = () => { setPendingCancelOrders(null); setIsModalOpen(true); };

  // ─── Receipt claim (transferred tab) ───────────────────────────────────────────
  const isPdf = (url) => /\.pdf($|\?)/i.test(url || '');
  // Locked to whoever claimed it until they finish or release it. `claimedByMe` is
  // computed by the backend (the auth user object carries no id).
  const claimedByOther = (r) => r.claimedByUserId && !r.claimedByMe;

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text || ''); toast.success('คัดลอกแล้ว'); }
    catch { toast.error('คัดลอกไม่สำเร็จ'); }
  };

  // Opening the receipt CLAIMS the case (locks others out before they start working).
  // Only opens the modal if we actually got the lock.
  // If another admin holds the case, confirm before taking it over; otherwise claim
  // and open straight away. confirmTakeover ({ message, onConfirm }) drives the dialog.
  const [confirmTakeover, setConfirmTakeover] = useState(null);
  const doOpenReceipt = async (record, takeover) => {
    const ok = await claimRefund(record.id, takeover);
    if (ok) setReceiptRecord(record);
  };
  const handleOpenReceipt = (record) => {
    if (claimedByOther(record)) {
      setConfirmTakeover({
        message: `กำลังดำเนินการโดย ${record.claimedByName || 'แอดมินท่านอื่น'}\nยืนยันจะดำเนินการต่อแทนหรือไม่?`,
        onConfirm: () => doOpenReceipt(record, true),
      });
      return;
    }
    doOpenReceipt(record, false);
  };

  // Closing without finishing releases the claim, so the case unlocks for others
  // and reappears in the keyData pending banner.
  const handleCloseReceipt = async () => {
    const rec = receiptRecord;
    setReceiptRecord(null);
    if (rec) await releaseRefund(rec.id);
  };

  // "แจ้งลูกค้าแล้ว" completes the case (backend clears the claim); just close on success.
  const handleNotifyFromModal = async () => {
    const rec = receiptRecord;
    if (!rec) return;
    const ok = await markCustomerNotified(rec.id);
    if (ok) { setReceiptRecord(null); toast.success('แจ้งลูกค้าเรียบร้อย'); }
  };

  // ─── Cancellation tab ─────────────────────────────────────────────────────────
  const daysSince = (date) => Math.ceil(Math.abs(new Date() - new Date(date)) / 86400000);

  // group a customer's cancelled orders so they can be refunded together
  // Credit-card void-lane orders are handled in their own sections — keep them OUT of the
  // normal refund/transfer groups so a customer's refund/inform summary never mixes one in.
  //   void_check  → owner uploads proof + voids (superuser section)
  //   void_notify → voided; admin notifies the customer (all-admins section)
  const voidCheckOrders = (orderGroup || []).filter((o) => o.cancelStage === 'void_check');
  // One Beam charge can cover several orders, so group the void-lane orders by their
  // beamPurchaseId — one card = one charge = ONE void action (voiding it settles them all).
  const voidCheckGroups = Object.values(voidCheckOrders.reduce((acc, o) => {
    const key = o.beamPurchaseId || `order:${o.id}`;
    (acc[key] = acc[key] || { key, beamPurchaseId: o.beamPurchaseId, orders: [] }).orders.push(o);
    return acc;
  }, {}));
  const voidNotifyOrders = (orderGroup || []).filter((o) => o.cancelStage === 'void_notify');
  const cancelGroups = Object.values((orderGroup || []).filter((o) => o.cancelStage !== 'void_check' && o.cancelStage !== 'void_notify').reduce((acc, o) => {
    const g = acc[o.customerId] || (acc[o.customerId] = {
      customerId: o.customerId,
      contact: o.CustomerProfile?.contact,
      smileName: o.smileNameInput,
      orders: [],
    });
    g.orders.push(o);
    return acc;
  }, {}));

  // Open the create modal pre-loaded with the given cancelled orders. When a group
  // has any paid order, ALL of its cancelled orders are carried in (unpaid ones ride
  // along at ฿0) so they close together and the customer is notified once.
  const openRefundForGroup = (orders) => {
    if (!orders.length) return;
    if (!window.confirm(`ยืนยันว่า ${orders.length} ออเดอร์นี้ยกเลิกจริง?`)) return;
    setPendingCancelOrders(orders);
    setIsModalOpen(true);
  };

  // Auto-claim the customer's cancel case when an admin starts an action on it, so two
  // admins don't process the same case. If already held by someone else, block + toast.
  // Claim holds until the case is done (or released) — no auto-release.
  const cancelGroupClaim = (g) => g.orders.find((o) => o.cancelClaimedByUserId);
  const cancelGroupMine = (g) => g.orders.some((o) => o.cancelClaimedByMe);
  const doClaimThen = async (g, fn, takeover) => {
    const res = await claimCancelCase(g.customerId, takeover);
    if (!res.ok) { toast.error(res.message); return; }
    fn();
  };
  const handleClaimThen = (g, fn) => {
    if (cancelGroupMine(g)) { fn(); return; }
    const heldByOther = cancelGroupClaim(g);
    if (heldByOther) {
      setConfirmTakeover({
        message: `กำลังดำเนินการโดย ${heldByOther.cancelClaimedByName || 'แอดมินท่านอื่น'}\nยืนยันจะดำเนินการต่อแทนหรือไม่?`,
        onConfirm: () => doClaimThen(g, fn, true),
      });
      return;
    }
    doClaimThen(g, fn, false);
  };

  // After a refund is created from the cancel tab, close every order it covered
  // (moves them out of the queue together); otherwise it's a manual refund.
  // Either way, land on รอข้อมูลบัญชี with the new record highlighted.
  const handleCreated = async (created) => {
    // Keep-as-credit: the backend already completed the whole cancellation atomically
    // (credit + order flip + cancelNote 'เก็บเป็นยอดฝากไว้แทนการโอนคืน') — running the
    // completion loop here would overwrite that note, and there's no refund row to
    // show on รอข้อมูลบัญชี, so refresh in place and stay on the current tab.
    //
    // Checked BEFORE the case-ticket hand-off below: a kept credit returns no refund
    // id, so that hand-off would try to link `undefined` to the case. Its reference is
    // cleared here too — left set, the NEXT refund created in this session would be
    // linked to this case instead.
    if (created?.keptAsCredit) {
      caseTicketIdRef.current = null;
      setCaseTicketPrefill(null);
      if (pendingCancelOrders && pendingCancelOrders.length) {
        setPendingCancelOrders(null);
        await getCancelledOrders();
      }
      fetchRecords();
      return;
    }
    // Case ticket hand-off: link the new refund back to the case and close it there.
    // On success we land back on the case; on failure the refund still exists, so
    // fall through to the normal landing steps (highlight + list) so it's visible
    // and the admin doesn't re-create a duplicate.
    if (caseTicketIdRef.current) {
      const ticketId = caseTicketIdRef.current;
      caseTicketIdRef.current = null;
      setCaseTicketPrefill(null);
      try {
        await CaseTicketService.linkRefund(ticketId, created.id);
        toast.success('เชื่อมรายการโอนคืนกับเคสแล้ว');
        navigate(`/case-ticket/${ticketId}`);
        return;
      } catch (err) {
        toast.error(`สร้างรายการโอนคืนสำเร็จ (#${created.id}) แต่เชื่อมกับเคส #${ticketId} ไม่สำเร็จ — ${err.response?.data?.message || 'กรุณาเชื่อมเคสอีกครั้งจากหน้าเคส'}`);
      }
    }
    if (pendingCancelOrders && pendingCancelOrders.length) {
      // Complete each cancelled order. Surface a partial failure (refund already exists)
      // so any straggler still in the cancel tab is visible, not silently left behind.
      try {
        for (const o of pendingCancelOrders) {
          await processCancellation({ orderId: o.id, action: 'complete', adminId: authState.user?.id, note: 'สร้างรายการโอนเงินคืนแล้ว' });
        }
      } catch (err) {
        toast.error('บางออเดอร์ปิดงานยกเลิกไม่สำเร็จ — กรุณาตรวจสอบที่แท็บรายการยกเลิก');
      }
      setPendingCancelOrders(null);
      await getCancelledOrders();
    }
    setHighlightId(created?.id || null);
    fetchRecords();
    setActiveTab('pending_info');
  };

  // Cancellation notice for unpaid orders (no refund) — admin pastes this to the customer.
  const buildCancelNotice = (orders) => [
    'ขออนุญาตแจ้งว่าออเดอร์ดังต่อไปนี้ถูกยกเลิกค่ะ',
    orders.map((o) => o.url).filter(Boolean).join('\n'),
    '',
    'ต้องขออภัยในความไม่สะดวกด้วยนะคะ',
  ].join('\n');

  // Step 1: confirm in a modal that the customer was informed (logs who via
  // AdminActivityLog 'cancel_informed_customer'), which marks the orders informed.
  const confirmInform = async () => {
    const orders = informGroup || [];
    if (!orders.length) { setInformGroup(null); return; }
    for (const o of orders) {
      await processCancellation({ orderId: o.id, action: 'informed', adminId: authState.user?.id });
    }
    setInformGroup(null);
    toast.success('บันทึกว่าแจ้งลูกค้าแล้ว');
    await getCancelledOrders();
  };

  // Step 2: close (no refund) — only offered after the customer was informed.
  const closeUnpaidInformed = async (orders) => {
    if (!orders.length) return;
    if (!window.confirm(`ปิดงาน ${orders.length} ออเดอร์ (ไม่ต้องคืนเงิน)?`)) return;
    for (const o of orders) {
      await processCancellation({ orderId: o.id, action: 'complete', adminId: authState.user?.id, note: 'ยกเลิกแล้ว — ไม่ต้องคืนเงิน (ลูกค้ายังไม่ได้ชำระ)' });
    }
    toast.success('ปิดงานยกเลิกแล้ว');
    await getCancelledOrders();
  };

  const revertCancel = async (order) => {
    const ok = await processCancellation({ orderId: order.id, action: 'revert', adminId: authState.user?.id });
    if (ok) { toast.success('คืนสถานะแล้ว (ไม่ได้โดนยกเลิก)'); await getCancelledOrders(); }
  };

  // Void-lane actions from the cancel tab:
  //   'reject'  (superuser, void_check)  → void ไม่ได้ → normal refund (+3.5%)
  //   'customer_notified' (admin, void_notify) → close as voided after telling the customer
  // The actual void + proof upload happens on the void page (link below / from LINE).
  const handleVoidResolve = async (order, action) => {
    if (action === 'reject' && !window.confirm('ยืนยันว่า void ไม่ได้? จะส่งเข้ารายการคืนเงินปกติ (+3.5%)')) return;
    if (action === 'customer_notified' && !window.confirm('ยืนยันว่าแจ้งลูกค้าเรื่องยกเลิก + คืนเงินเข้าบัตรแล้ว?')) return;
    const ok = await resolveVoidCheck(order.id, action);
    if (ok) {
      toast.success(action === 'customer_notified' ? 'ปิดเคสแล้ว' : 'ส่งเข้ารายการคืนเงินปกติแล้ว');
      await getCancelledOrders();
      // "Void ไม่ได้ → คืนเงิน" means: refund it now. Open the refund window for this
      // order straight away (claiming the case first, like the normal button) instead
      // of leaving the admin to find it in the list and confirm a second time (QA 809).
      if (action === 'reject') {
        const claim = await claimCancelCase(order.customerId);
        if (!claim.ok) { toast.error(claim.message); return; }
        setPendingCancelOrders([order]);
        setIsModalOpen(true);
      }
    } else {
      toast.error('ดำเนินการไม่สำเร็จ');
    }
  };

  // Customer message after a void (money already returned to the card).
  const buildVoidNotice = (order) => [
    'ขออนุญาตแจ้งว่าออเดอร์ดังต่อไปนี้ถูกยกเลิกค่ะ',
    order.url || '',
    '',
    'ทางเราได้ทำการคืนเงินเข้าบัตรเครดิตของลูกค้าเรียบร้อยแล้ว (ตามหลักฐานแนบ)',
    'ยอดจะกลับเข้าบัตรภายใน 1-2 รอบบิลตามรอบของธนาคารนะคะ',
    '',
    'ต้องขออภัยในความไม่สะดวกด้วยค่ะ',
  ].join('\n');

  // ─── Bank form ────────────────────────────────────────────────────────────────
  const setBankField = (id, field, value) =>
    setBankForms((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const handleSaveBank = async (id) => {
    const info = bankForms[id] || {};
    if (!info.bankName || !info.accountNumber || !info.accountName) {
      toast.error('กรุณากรอกข้อมูลธนาคารให้ครบ'); return;
    }
    if (!/^\d+$/.test(info.accountNumber)) {
      toast.error('เลขบัญชีต้องเป็นตัวเลขเท่านั้น'); return;
    }
    if (/^\d+$/.test(info.accountName.trim())) {
      toast.error('ชื่อบัญชีต้องเป็นชื่อ ไม่ใช่ตัวเลข — กรุณาตรวจสอบว่าไม่ได้กรอกสลับช่อง'); return;
    }
    setBankSaving((prev) => ({ ...prev, [id]: true }));
    const ok = await saveBankInfo(id, info);
    setBankSaving((prev) => ({ ...prev, [id]: false }));
    if (ok) {
      toast.success('บันทึกข้อมูลธนาคารสำเร็จ');
      setBankForms((prev) => { const n = { ...prev }; delete n[id]; return n; });
      setOpenBankForms((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  // Customer sent a PromptPay QR image instead of typed bank details.
  const handleUploadQr = async (id, file) => {
    if (!file) return;
    setQrUploading((prev) => ({ ...prev, [id]: true }));
    const ok = await uploadBankQr(id, file);
    setQrUploading((prev) => ({ ...prev, [id]: false }));
    if (ok) {
      toast.success('บันทึก QR พร้อมเพย์แล้ว');
      setOpenBankForms((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  const handleQrConfirm = (files) => {
    const id = qrModalRecordId;
    if (id && files && files[0]) handleUploadQr(id, files[0]);
  };

  // Send a refund back to รอข้อมูลบัญชี to fix the bank info. The owner's existing
  // link stays valid and will show the corrected info (no new LINE is sent), so we
  // prefill the form with the current values for editing.
  const handleRevert = async (record) => {
    if (!window.confirm('ย้อนกลับไปแก้ไขข้อมูลบัญชี? (ลิงก์เดิมของหัวหน้าจะแสดงข้อมูลใหม่อัตโนมัติ)')) return;
    const ok = await revertToPending(record.id);
    if (ok) {
      setBankForms((prev) => ({ ...prev, [record.id]: {
        bankName: record.bankName || '',
        accountNumber: record.accountNumber || '',
        accountName: record.accountName || '',
      } }));
      setOpenBankForms((prev) => ({ ...prev, [record.id]: true }));
      setActiveTab('pending_info');
    }
  };

  // ─── Convert to credit (customer changed their mind) ────────────────────────
  // Credits the customer's advanceAmount instead of transferring; the refund is
  // closed (soft-deleted) and the manager's transfer link flips to "cancelled".
  const handleConvertToCredit = async (record) => {
    if (!window.confirm(`ลูกค้าเปลี่ยนใจ เก็บ ${formatAmount(record.amount)} บาท เป็นยอดฝากไว้แทนการโอนคืน?\n(ยอดฝากของลูกค้าจะเพิ่มขึ้นทันที และรายการโอนคืนนี้จะถูกปิด)`)) return;
    // Without cancelled orders behind the refund (or reason สรุปผิด, whose orders are
    // active) the note is the required evidence — prompt when the row lacks one.
    const noteBacked = record.reason === 'admin_mistake' || (record.orderIds || []).length === 0;
    let note;
    if (noteBacked && !record.note?.trim()) {
      note = window.prompt('ระบุรายละเอียด (จำเป็น) เช่น คำนวณค่าส่งเกิน 70 บาท');
      if (!note?.trim()) { toast.error('ต้องระบุรายละเอียดก่อนเก็บเป็นยอดฝาก'); return; }
    }
    const result = await convertToCredit(record.id, note?.trim());
    if (result) toast.success(`เก็บเป็นยอดฝากไว้แล้ว — ยอดฝากปัจจุบัน ${formatAmount(result.advanceAmount)} บาท`);
  };

  // ─── Delete (soft-delete) ───────────────────────────────────────────────────
  // Deleting keeps the row (audit) but requires a reason so a mistaken delete is
  // distinguishable in the report. Refreshing the report after delete keeps its
  // deleted section in sync if it's open.
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    if (!deleteReason.trim()) { toast.error('กรุณาระบุเหตุผลในการลบ'); return; }
    setDeleteSaving(true);
    const ok = await deleteRecord(deleteTarget.id, deleteReason.trim());
    setDeleteSaving(false);
    if (ok) {
      toast.success('ลบรายการแล้ว');
      setDeleteTarget(null);
      setDeleteReason('');
      if (activeTab === 'report') fetchReport().then(setReportData);
    }
  };

  // ─── Edit amount (before money moves) ───────────────────────────────────────
  const handleConfirmAmount = async () => {
    if (!amountTarget) return;
    const amt = parseFloat(amountValue);
    if (!(amt > 0)) { toast.error('กรุณากรอกยอดที่ถูกต้อง'); return; }
    setAmountSaving(true);
    const ok = await updateAmount(amountTarget.id, amt);
    setAmountSaving(false);
    if (ok) {
      toast.success('แก้ไขยอดแล้ว');
      setAmountTarget(null);
      setAmountValue('');
    }
  };

  // ─── Audit search: trace a refund flow by order URL ─────────────────────────
  const handleFlowSearch = async () => {
    if (!flowQuery.trim()) return;
    setFlowSearching(true);
    const data = await searchFlow(flowQuery.trim());
    setFlowSearching(false);
    setFlowResults(data?.results || []);
  };

  const handleCopyNotify = (record) => {
    const orders = Array.isArray(record.orders) ? record.orders : [];
    const amount = formatAmount(record.amount);

    const linkLines = orders.map((o) => o.url).filter(Boolean).join('\n');

    // For "อื่นๆ" leave the title blank — the admin's internal note may be
    // informal and should never appear in the customer-facing message.
    const reasonTitle = record.reason === 'other'
      ? ''
      : REASON_LABELS[record.reason] || record.reason || '';

    const base = [
      `ขออนุญาตแจ้งยอดโอนคืนของรายการ`,
      linkLines,
      ``,
    ];
    const bankReq = [
      `รบกวนขอข้อมูลสำหรับการโอนเงินคืนค่ะ :`,
      `- ชื่อธนาคาร`,
      `- เลขบัญชี`,
      `- ชื่อบัญชี`,
      ``,
    ];

    const REASON_TEMPLATES = {
      cancelled:  [...([reasonTitle]), ...base, `โอนคืนจำนวน ${amount} บาทค่ะ (สินค้าถูกยกเลิกโดยผู้ขาย)`, ...bankReq, `ขอบคุณมากค่ะ และขออภัยในความไม่สะดวกด้วยนะคะ`],
      damaged:    [...([reasonTitle]), ...base, `โอนคืนจำนวน ${amount} บาทค่ะ (ช่วยเรื่องสินค้าเสียหาย)`, ...bankReq, `ขอบคุณมากค่ะ และขออภัยในความไม่สะดวกด้วยนะคะ`],
      not_found:  [...([reasonTitle]), ...base, `โอนคืนจำนวน ${amount} บาทค่ะ (สินค้าสูญหาย)`, ...bankReq, `ทั้งนี้หากทางโกดังเจอสินค้าแล้วจะรีบแจ้งไปโดยเร็วที่สุดค่ะ`, ``, `ต้องขออภัยในความไม่สะดวกด้วยค่ะ`],
      late_order: [...([reasonTitle]), ...base, `โอนคืนจำนวน ${amount} บาทค่ะ (สินค้าขายออกไปแล้ว)`, ...bankReq, `ขอบคุณมากค่ะ และขออภัยในความไม่สะดวกด้วยนะคะ`],
      admin_mistake: [...([reasonTitle]), ...base, `โอนคืนจำนวน ${amount} บาทค่ะ (ทางร้านสรุปยอดผิด ต้องขออภัยด้วยนะคะ)`, ...bankReq, `ขอบคุณมากค่ะ และขออภัยในความไม่สะดวกด้วยนะคะ`],
    };

    const lines = REASON_TEMPLATES[record.reason] || [
      ...(reasonTitle ? [reasonTitle] : []),
      ...base,
      `โอนคืนจำนวน ${amount} บาทค่ะ`,
      ...bankReq,
      `ขอบคุณมากค่ะ และขออภัยในความไม่สะดวกด้วยนะคะ`,
    ];

    navigator.clipboard.writeText(lines.join('\n')).then(() => toast.success('คัดลอกข้อความแจ้ง ลค แล้ว'));
  };

  // ─── Record card ──────────────────────────────────────────────────────────────
  const renderCard = (record) => {
    const orders = Array.isArray(record.orders) ? record.orders : [];
    const reasonLabel = REASON_LABELS[record.reason] || record.reason;

    const isNew = record.id === highlightId;
    const isWithdrawal = !!record.withdrawalRequestId;
    const bMode = bankMode[record.id] || 'bank';
    return (
      <div key={record.id} className={`border rounded-lg bg-white overflow-hidden mb-4 ${isNew ? 'border-green-400 ring-2 ring-green-300' : isWithdrawal ? 'border-purple-300 ring-1 ring-purple-200' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between px-5 py-3 bg-gray-50 flex-wrap gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            {isNew && <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">● เพิ่งสร้าง</span>}
            <span className="font-bold text-lg">{record.contact || '-'}</span>
            {record.smileName && <span className="text-gray-500 text-sm">({record.smileName})</span>}
            <span className="font-semibold text-blue-600 text-lg">
              {formatAmount(record.amount)} บาท
            </span>
            {activeTab === 'pending_info' && (
              <button onClick={() => { setAmountTarget(record); setAmountValue(String(record.amount ?? '')); }}
                title="แก้ไขยอด"
                className="text-blue-500 hover:text-blue-700 text-sm border border-blue-200 hover:border-blue-300 rounded px-1.5 py-0.5">
                ✎ แก้ไขยอด
              </button>
            )}
            {reasonLabel && (
              <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full font-medium">
                {reasonLabel}
              </span>
            )}
            {record.withdrawalRequestId && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full font-semibold">
                💸 ถอนเงิน (Withdrawal)
              </span>
            )}
            <span className="text-gray-400 text-sm">สร้างเมื่อ {formatDate(record.createdAt)}</span>
            {(activeTab === 'transferred' || activeTab === 'customer_notified') && record.transferredAt && (
              <span className="text-green-600 text-sm font-medium">โอนเมื่อ {formatDate(record.transferredAt)}</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {activeTab === 'pending_info' && (
              <button onClick={() => handleCopyNotify(record)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                คัดลอกข้อความแจ้ง ลค
              </button>
            )}
            {activeTab === 'ready_to_transfer' && (
              <button onClick={() => handleRevert(record)}
                className="bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded text-sm font-medium">
                ↩️ แก้ไขข้อมูล
              </button>
            )}
            {activeTab === 'customer_notified' && record.slipUrl && (
              <a href={record.slipUrl} target="_blank" rel="noopener noreferrer"
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                ดูสลิป
              </a>
            )}
            {activeTab === 'transferred' && (
              <>
                {record.claimedByUserId && (
                  <span className={`px-3 py-1.5 rounded text-sm font-medium ${record.claimedByMe ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                    🔒 ดำเนินการโดย {record.claimedByMe ? 'คุณ' : (record.claimedByName || 'แอดมินท่านอื่น')}
                  </span>
                )}
                <button onClick={() => handleOpenReceipt(record)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                  📄 ดูสลิป / แจ้งลูกค้า
                </button>
              </>
            )}
            {/* Convert to credit only before the money moves and never for company
                customers (no advanceAmount credit for companies). Works for every
                reason: order-backed when the refund references cancelled orders,
                note-backed otherwise. */}
            {(record.status === 'pending_info' || record.status === 'ready_to_transfer') && !record.isCompanyCustomer && (
              <button onClick={() => handleConvertToCredit(record)}
                className="bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded text-sm font-medium">
                💰 เก็บเป็นยอดฝาก
              </button>
            )}
            {/* Delete only before the money moves — backend blocks transferred/notified. */}
            {(record.status === 'pending_info' || record.status === 'ready_to_transfer') && (
              <button onClick={() => { setDeleteTarget(record); setDeleteReason(''); }}
                className="bg-white border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded text-sm font-medium">
                🗑️ ลบ
              </button>
            )}
          </div>
        </div>

        {record.bankName && (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-sm flex gap-4 flex-wrap items-center">
            <BankBadge bankName={record.bankName} />
            <span>เลขบัญชี: <span className="font-mono font-semibold">{record.accountNumber}</span></span>
            <span>ชื่อบัญชี: {record.accountName}</span>
          </div>
        )}

        {record.qrUrl && (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-sm flex gap-3 flex-wrap items-center">
            <span className="font-medium text-blue-800">QR พร้อมเพย์:</span>
            <img src={record.qrUrl} alt="PromptPay QR" onClick={() => setZoomSrc(record.qrUrl)}
              className="h-24 w-24 object-contain border border-gray-200 rounded bg-white cursor-pointer hover:opacity-80" />
          </div>
        )}

        {(orders.length > 0 || record.hasManualItems) && (
          <div className="px-5 py-3 border-b border-gray-100">
            <p className="text-xs text-gray-500 mb-1 font-semibold uppercase tracking-wide">
              รายการสินค้า
              {record.hasManualItems && (
                <span className="ml-2 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded normal-case">มีรายการกรอกเอง</span>
              )}
            </p>
            <div className="flex flex-col gap-1">
              {orders.map((o) => (
                <a key={o.id} href={o.url} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm break-all">{o.url}</a>
              ))}
            </div>
            {record.note && (
              <p className="text-xs text-gray-500 mt-1">หมายเหตุ: {record.note}</p>
            )}
          </div>
        )}

        {activeTab === 'pending_info' && (
          <div className="border-t border-orange-100">
            <button
              onClick={() => setOpenBankForms((prev) => ({ ...prev, [record.id]: !prev[record.id] }))}
              className="w-full flex items-center justify-between px-5 py-3 bg-orange-50 hover:bg-orange-100 transition-colors text-sm font-semibold text-orange-800">
              <span>กรอกข้อมูลบัญชีธนาคาร (เมื่อลูกค้าแจ้งมา)</span>
              <span className="text-orange-500 text-lg">{openBankForms[record.id] ? '▲' : '▼'}</span>
            </button>

            {openBankForms[record.id] && (
              <div className="px-5 py-4 bg-orange-50">
                {/* mode toggle: typed bank details vs PromptPay QR image */}
                <div className="flex gap-2 mb-3">
                  <button type="button" onClick={() => setBankMode((p) => ({ ...p, [record.id]: 'bank' }))}
                    className={`px-3 py-1.5 rounded text-sm font-medium border ${bMode !== 'qr' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300'}`}>เลขบัญชี</button>
                  <button type="button" onClick={() => setBankMode((p) => ({ ...p, [record.id]: 'qr' }))}
                    className={`px-3 py-1.5 rounded text-sm font-medium border ${bMode === 'qr' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300'}`}>QR พร้อมเพย์</button>
                </div>

                {bMode !== 'qr' ? (
                  <>
                    <div className="mb-3">
                      <label className="block text-xs text-gray-600 mb-2">ธนาคาร <span className="text-red-500">*</span></label>
                      <div className="grid grid-cols-5 gap-2">
                        {BANKS.map((bank) => (
                          <BankLogoButton key={bank.name} bank={bank}
                            selected={bankForms[record.id]?.bankName === bank.name}
                            onClick={() => setBankField(record.id, 'bankName', bank.name)} />
                        ))}
                      </div>
                      {bankForms[record.id]?.bankName && (
                        <p className="text-xs text-blue-700 mt-1">เลือก: {bankForms[record.id].bankName}</p>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-3 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600">เลขบัญชี <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="ตัวเลขเท่านั้น" inputMode="numeric"
                          className="border border-gray-300 rounded px-2 py-1.5 text-sm w-44"
                          value={bankForms[record.id]?.accountNumber || ''}
                          onChange={(e) => setBankField(record.id, 'accountNumber', e.target.value.replace(/\D/g, ''))} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-gray-600">ชื่อบัญชี <span className="text-red-500">*</span></label>
                        <input type="text" placeholder="ชื่อ-นามสกุล"
                          className={`border rounded px-2 py-1.5 text-sm w-44 ${/^\d+$/.test((bankForms[record.id]?.accountName || '').trim()) ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                          value={bankForms[record.id]?.accountName || ''}
                          onChange={(e) => setBankField(record.id, 'accountName', e.target.value)} />
                        {/^\d+$/.test((bankForms[record.id]?.accountName || '').trim()) && (
                          <span className="text-xs text-red-500">ชื่อบัญชีไม่ควรเป็นตัวเลข</span>
                        )}
                      </div>
                      <button onClick={() => handleSaveBank(record.id)} disabled={bankSaving[record.id]}
                        className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-4 py-1.5 rounded text-sm font-medium">
                        {bankSaving[record.id] ? 'กำลังบันทึก...' : 'บันทึก'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-xs text-gray-600 mb-2">แนบรูป QR พร้อมเพย์ที่ลูกค้าส่งมา (ใช้แทนเลขบัญชี)</p>
                    {record.qrUrl && (
                      <img src={record.qrUrl} alt="PromptPay QR" onClick={() => setZoomSrc(record.qrUrl)}
                        className="h-24 w-24 object-contain border border-gray-200 rounded bg-white mb-2 cursor-pointer hover:opacity-80" />
                    )}
                    <button onClick={() => setQrModalRecordId(record.id)} disabled={qrUploading[record.id]}
                      className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-4 py-1.5 rounded text-sm font-medium">
                      {qrUploading[record.id] ? 'กำลังอัปโหลด...' : (record.qrUrl ? '📷 เปลี่ยนรูป QR' : '📷 แนบรูป QR พร้อมเพย์')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ─── Report tab ───────────────────────────────────────────────────────────────
  const renderReport = () => {
    if (!reportData) return <div className="text-center py-10 text-gray-500">กำลังโหลด...</div>;

    const { records: allRecs, deletedRecords = [], deletedCount = 0, deletedTotalAmount = 0 } = reportData;

    const recs = allRecs.filter((r) => {
      const date = r.transferredAt ? new Date(r.transferredAt) : null;
      if (reportFilter.startDate && date && date < new Date(reportFilter.startDate)) return false;
      if (reportFilter.endDate && date && date > new Date(reportFilter.endDate + 'T23:59:59')) return false;
      if (reportFilter.reason && r.reason !== reportFilter.reason) return false;
      return true;
    });

    const summary = {};
    let totalAmount = 0;
    for (const r of recs) {
      const key = r.reason || 'ไม่ระบุ';
      if (!summary[key]) summary[key] = { count: 0, total: 0 };
      summary[key].count++;
      summary[key].total += parseFloat(r.amount || 0);
      totalAmount += parseFloat(r.amount || 0);
    }
    const totalCount = recs.length;

    return (
      <div>
        {/* Audit search: trace a refund flow (live + deleted) by order URL */}
        <div className="mb-5 p-3 bg-slate-50 border border-slate-200 rounded-lg">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-600">🔎 ค้นหาด้วย Link สินค้า:</span>
            <input type="text" value={flowQuery}
              onChange={(e) => setFlowQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFlowSearch()}
              placeholder="วาง Link สินค้าเพื่อดูประวัติการคืนเงิน (รวมที่ถูกลบ)"
              className="flex-1 min-w-[260px] border border-gray-300 rounded px-3 py-1.5 text-sm" />
            <button onClick={handleFlowSearch} disabled={flowSearching || !flowQuery.trim()}
              className="bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white px-4 py-1.5 rounded text-sm font-medium">
              {flowSearching ? 'กำลังค้นหา...' : 'ค้นหา'}
            </button>
            {flowResults !== null && (
              <button onClick={() => { setFlowResults(null); setFlowQuery(''); }}
                className="text-xs text-gray-500 hover:text-gray-700 underline">ล้าง</button>
            )}
          </div>

          {flowResults !== null && (
            <div className="mt-3">
              {flowResults.length === 0 ? (
                <p className="text-sm text-gray-400">ไม่พบรายการคืนเงินสำหรับ Link นี้</p>
              ) : (
                <div className="flex flex-col gap-2">
                  {flowResults.map((r) => (
                    <div key={r.id} className={`border rounded p-3 text-sm ${r.isDeleted ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold">{r.contact || '-'}</span>
                        {r.smileName && <span className="text-gray-400 text-xs">({r.smileName})</span>}
                        <span className="font-semibold text-blue-700">฿{formatAmount(r.amount)}</span>
                        {r.reason && REASON_LABELS[r.reason] && (
                          <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">{REASON_LABELS[r.reason]}</span>
                        )}
                        {r.isDeleted ? (
                          <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">ถูกลบ</span>
                        ) : (
                          <span className="bg-gray-200 text-gray-700 text-xs px-2 py-0.5 rounded-full">{STATUS_LABELS[r.status] || r.status}</span>
                        )}
                        <span className="text-gray-400 text-xs ml-auto">สร้าง {formatDate(r.createdAt)}</span>
                      </div>
                      {r.isDeleted && (
                        <p className="text-xs text-red-600 mt-1">
                          ลบโดย {r.deletedByName || '-'} เมื่อ {formatDate(r.deletedAt)}
                          {r.deleteReason && <> — เหตุผล: {r.deleteReason}</>}
                        </p>
                      )}
                      {Array.isArray(r.orders) && r.orders.length > 0 && (
                        <div className="flex flex-col gap-0.5 mt-1">
                          {r.orders.map((o) => (
                            <a key={o.id} href={o.url} target="_blank" rel="noopener noreferrer"
                              className="text-blue-600 hover:underline text-xs break-all">{o.url}</a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-sm font-semibold text-gray-600">กรอง:</span>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">จาก</label>
            <input type="date" value={reportFilter.startDate}
              onChange={(e) => setReportFilter((p) => ({ ...p, startDate: e.target.value }))}
              className="border border-gray-300 rounded px-2 py-1 text-sm" />
          </div>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">ถึง</label>
            <input type="date" value={reportFilter.endDate}
              onChange={(e) => setReportFilter((p) => ({ ...p, endDate: e.target.value }))}
              className="border border-gray-300 rounded px-2 py-1 text-sm" />
          </div>
          <select value={reportFilter.reason}
            onChange={(e) => setReportFilter((p) => ({ ...p, reason: e.target.value }))}
            className="border border-gray-300 rounded px-2 py-1 text-sm">
            <option value="">ทุกประเภท</option>
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {(reportFilter.startDate || reportFilter.endDate || reportFilter.reason) && (
            <button onClick={() => setReportFilter({ startDate: '', endDate: '', reason: '' })}
              className="text-xs text-red-500 hover:text-red-700 underline">ล้างตัวกรอง</button>
          )}
          <span className="ml-auto text-xs text-gray-400">แสดง {totalCount} รายการ</span>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600 font-medium">รวมทั้งหมด</p>
            <p className="text-2xl font-bold text-blue-700">{totalCount} รายการ</p>
            <p className="text-sm text-blue-600">{formatAmount(totalAmount)} บาท</p>
          </div>
          {REASONS.map((r) => {
            const data = summary[r.value] || { count: 0, total: 0 };
            return (
              <div key={r.value} className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <p className="text-sm text-orange-600 font-medium">{r.label}</p>
                <p className="text-2xl font-bold text-orange-700">{data.count} รายการ</p>
                <p className="text-sm text-orange-600">{formatAmount(data.total)} บาท</p>
              </div>
            );
          })}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
            <p className="text-sm text-red-600 font-medium">🗑️ ถูกลบ</p>
            <p className="text-2xl font-bold text-red-700">{deletedCount} รายการ</p>
            <p className="text-sm text-red-600">{formatAmount(deletedTotalAmount)} บาท</p>
          </div>
        </div>

        {/* Records table */}
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
            <thead>
              <tr className="bg-gray-100 text-sm">
                <th className="px-4 py-2 text-left font-semibold text-gray-700">วันที่โอน</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">ลูกค้า</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">เหตุผล</th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700">ธนาคาร</th>
                <th className="px-4 py-2 text-right font-semibold text-gray-700">ยอด (บาท)</th>
                <th className="px-4 py-2 text-center font-semibold text-gray-700">สลิป</th>
              </tr>
            </thead>
            <tbody>
              {recs.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">ยังไม่มีรายการที่โอนแล้ว</td></tr>
              )}
              {recs.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50 text-sm">
                  <td className="px-4 py-2">{formatDate(r.transferredAt)}</td>
                  <td className="px-4 py-2">
                    <span className="font-medium">{r.contact || '-'}</span>
                    {r.smileName && <span className="text-gray-400 ml-1 text-xs">({r.smileName})</span>}
                  </td>
                  <td className="px-4 py-2">
                    {REASON_LABELS[r.reason] ? (
                      <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">
                        {REASON_LABELS[r.reason]}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2"><BankBadge bankName={r.bankName} /></td>
                  <td className="px-4 py-2 text-right font-semibold text-blue-700">
                    {formatAmount(r.amount)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {r.slipUrl ? (
                      <a href={r.slipUrl} target="_blank" rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-xs">ดูสลิป</a>
                    ) : <span className="text-gray-400 text-xs">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Deleted refunds — audit whether a deletion was a mistake */}
        {deletedCount > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-bold text-red-700 mb-2">🗑️ รายการที่ถูกลบ ({deletedCount})</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-red-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-red-50 text-sm">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">วันที่ลบ</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">ลูกค้า</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">เหตุผลคืนเงิน</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">ยอด (บาท)</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">ลบโดย</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">เหตุผลที่ลบ</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Link</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedRecords.map((r) => (
                    <tr key={r.id} className="border-t border-red-100 hover:bg-red-50/40 text-sm">
                      <td className="px-4 py-2 whitespace-nowrap">{formatDate(r.deletedAt)}</td>
                      <td className="px-4 py-2">
                        <span className="font-medium">{r.contact || '-'}</span>
                        {r.smileName && <span className="text-gray-400 ml-1 text-xs">({r.smileName})</span>}
                      </td>
                      <td className="px-4 py-2">{REASON_LABELS[r.reason] || <span className="text-gray-400">-</span>}</td>
                      <td className="px-4 py-2 text-right font-semibold text-blue-700">{formatAmount(r.amount)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{r.deletedByName || '-'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.deleteReason || '-'}</td>
                      <td className="px-4 py-2">
                        {Array.isArray(r.orders) && r.orders.length > 0 ? (
                          <div className="flex flex-col gap-0.5">
                            {r.orders.map((o) => (
                              <a key={o.id} href={o.url} target="_blank" rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-xs break-all max-w-xs truncate block">{o.url}</a>
                            ))}
                          </div>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Cancellation tab render ────────────────────────────────────────────────
  const renderCancelTab = () => (
    <>
      <p className="text-gray-500 mb-4">รายการที่ถูกยกเลิกจากหน้าเติมเลขพัสดุ — จัดกลุ่มตามลูกค้า กดสร้าง Refund รวมออเดอร์ที่จ่ายแล้ว หรือปิดงานที่ไม่ต้องคืน</p>
      {cancelGroups.length === 0 && voidNotifyOrders.length === 0 && voidCheckOrders.length === 0 ? (
        <div className="text-center py-10 text-gray-400 text-lg">ไม่มีรายการยกเลิก</div>
      ) : cancelGroups.map((g) => {
        const paidOrders = g.orders.filter((o) => PAID_STATUSES.includes(o.productPayment));
        const unpaid = g.orders.filter((o) => !PAID_STATUSES.includes(o.productPayment));
        const unpaidToInform = unpaid.filter((o) => o.cancelStage !== 'informed');
        const unpaidInformed = unpaid.filter((o) => o.cancelStage === 'informed');
        const processing = !!pendingCancelOrders && pendingCancelOrders.some((p) => p.customerId === g.customerId);
        const claimOrder = cancelGroupClaim(g);
        const mine = cancelGroupMine(g);
        return (
          <div key={g.customerId} className={`border rounded-lg mb-4 overflow-hidden ${processing ? 'border-green-400 ring-2 ring-green-300' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
              <span className="font-bold">{g.contact || g.smileName || '—'}</span>
              {g.smileName && g.contact && <span className="text-gray-500 text-sm">({g.smileName})</span>}
              <span className="text-gray-400 text-sm">— {g.orders.length} ออเดอร์ยกเลิก</span>
              {claimOrder && (
                <span className={`ml-auto px-2.5 py-1 rounded text-xs font-medium ${mine ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                  🔒 ดำเนินการโดย {mine ? 'คุณ' : (claimOrder.cancelClaimedByName || 'แอดมินท่านอื่น')}
                </span>
              )}
            </div>

            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 bg-white border-b border-gray-100">
                  <th className="px-4 py-1.5 text-left font-medium">วันที่</th>
                  <th className="px-4 py-1.5 text-left font-medium">ค้าง</th>
                  <th className="px-4 py-1.5 text-left font-medium">Link</th>
                  <th className="px-4 py-1.5 text-left font-medium">สถานะการชำระ</th>
                  <th className="px-4 py-1.5 text-right font-medium">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {g.orders.map((order) => {
                  const d = daysSince(order.updatedAt);
                  return (
                    <tr key={order.id} className={`border-t border-gray-100 ${d >= 2 ? 'bg-yellow-50' : ''}`}>
                      <td className="px-4 py-2 whitespace-nowrap text-gray-500">{formatDate(order.orderDate)}</td>
                      <td className="px-4 py-2 whitespace-nowrap"><span className={d >= 2 ? 'text-red-600 font-semibold' : 'text-gray-400'}>{d} วัน</span></td>
                      <td className="px-4 py-2"><a href={order.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs block">{order.url}</a></td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs border ${PRODUCT_PAYMENT_STYLE[order.productPayment] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                          {order.productPayment || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {order.cancelStage === 'informed' && <span className="text-green-600 text-xs mr-2">✓ แจ้งแล้ว</span>}
                        <button onClick={() => revertCancel(order)}
                          className="text-blue-500 hover:text-blue-700 text-xs underline">ไม่ได้ยกเลิก</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="flex flex-wrap gap-2 px-4 py-2 bg-gray-50 border-t border-gray-100 items-center">
              {(
                <>
                  {paidOrders.length > 0 ? (
                    // Has a paid order → carry the whole group (paid + unpaid) into one refund
                    <button onClick={() => handleClaimThen(g, () => openRefundForGroup(g.orders))}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                      สร้าง Refund รวมทุกออเดอร์ ({g.orders.length})
                      {unpaid.length > 0 && <span className="font-normal opacity-90"> — มี {unpaid.length} ที่ไม่มียอดคืน</span>}
                    </button>
                  ) : (
                    // Pure no-refund group → inform (modal) then close
                    <>
                      {unpaidToInform.length > 0 && (
                        <button onClick={() => handleClaimThen(g, () => setInformGroup(unpaidToInform))}
                          className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1.5 rounded text-sm font-medium">
                          แจ้งลูกค้ายกเลิก (ไม่คืนเงิน) ({unpaidToInform.length})
                        </button>
                      )}
                      {unpaidInformed.length > 0 && (
                        <>
                          {/* Re-send the notice: the 'informed' flag is a manual click and can't
                              prove the message was actually sent — so any admin can re-copy/resend. */}
                          <button onClick={() => handleClaimThen(g, () => setInformGroup(unpaidInformed))}
                            className="px-3 py-1.5 rounded text-sm border border-yellow-300 text-yellow-700 hover:bg-yellow-50 font-medium">
                            📋 ขอข้อความแจ้งลูกค้า อีกครั้ง
                          </button>
                          <button onClick={() => handleClaimThen(g, () => closeUnpaidInformed(unpaidInformed))}
                            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded text-sm font-medium">
                            ปิดงาน ไม่คืนเงิน ({unpaidInformed.length})
                          </button>
                          <span className="basis-full text-xs text-amber-600">⚠️ ปิดงานเฉพาะเมื่อลูกค้ารับทราบการยกเลิกแล้ว</span>
                        </>
                      )}
                    </>
                  )}
                  {mine && (
                    <button onClick={() => releaseCancelCase(g.customerId)}
                      className="ml-auto px-3 py-1.5 rounded text-sm border border-gray-300 text-gray-500 hover:bg-gray-100">ปล่อยเคส</button>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}

      {/* Section: credit-card orders to be voided — owner uploads proof on the void page (superuser only) */}
      {isSuperuser && voidCheckGroups.length > 0 && (
        <div className="mt-8 border-t border-orange-200 pt-4">
          <h3 className="text-lg font-bold text-orange-700 mb-1">💳 รอ void (บัตรเครดิต) ({voidCheckGroups.length})</h3>
          <p className="text-xs text-gray-500 mb-3">เปิดหน้า void เพื่อ void ที่ Beam แล้วแนบรูปยืนยัน — หรือถ้า void ไม่ได้ ส่งเข้ารายการคืนเงินปกติ (1 การชำระ = void ครั้งเดียว)</p>
          <div className="flex flex-col gap-2">
            {voidCheckGroups.map((group) => {
              const head = group.orders[0];
              return (
              <div key={group.key} className="border border-orange-200 bg-orange-50 rounded p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{head.CustomerProfile?.contact || head.smileNameInput || '—'}</span>
                  {group.orders.length > 1 && (
                    <span className="text-xs bg-orange-200 text-orange-800 rounded px-1.5 py-0.5">{group.orders.length} ออเดอร์ / จ่ายครั้งเดียว</span>
                  )}
                  <span className="text-gray-400 text-xs ml-auto">จ่ายเมื่อ {head.paymentCompletedAt ? formatDate(head.paymentCompletedAt) : '-'}</span>
                </div>
                <div className="flex flex-col gap-0.5 mt-1">
                  {group.orders.map((order) => (
                    <a key={order.id} href={order.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-md">{order.url}</a>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-xs text-gray-600">purchaseId:</span>
                  <code className="text-xs bg-white border border-gray-300 rounded px-2 py-0.5 break-all">{group.beamPurchaseId || '-'}</code>
                  <button onClick={() => copyText(group.beamPurchaseId)} className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white">คัดลอก</button>
                  <div className="ml-auto flex gap-2">
                    {head.voidCheckUrl && (
                      <a href={head.voidCheckUrl} target="_blank" rel="noopener noreferrer"
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium">🔎 เปิดหน้า void + แนบรูป</a>
                    )}
                    <button onClick={() => handleVoidResolve(head, 'reject')}
                      className="px-3 py-1 rounded text-sm border border-gray-300 text-gray-600 hover:bg-gray-100">❌ Void ไม่ได้ → คืนเงิน</button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section: void_check info for non-superusers — read-only, waiting on the owner.
          NOT an admin task: no actions, no links, no notify — just visibility so the
          tab count isn't a phantom. */}
      {!isSuperuser && voidCheckOrders.length > 0 && (
        <div className="mt-8 border-t border-gray-200 pt-4">
          <h3 className="text-lg font-semibold text-gray-500 mb-1">💳 รอเจ้าของร้านเช็ค void (บัตรเครดิต) ({voidCheckOrders.length})</h3>
          <p className="text-xs text-gray-400 mb-3">รายการนี้รอเจ้าของร้านตัดสินใจ — ไม่ต้องดำเนินการ</p>
          <div className="flex flex-col gap-2">
            {voidCheckOrders.map((order) => (
              <div key={order.id} className="border border-gray-200 bg-gray-50 rounded p-3 text-sm text-gray-500">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{order.CustomerProfile?.contact || order.smileNameInput || '—'}</span>
                  <span className="truncate max-w-xs">{order.url}</span>
                  <span className="text-gray-400 text-xs ml-auto">จ่ายเมื่อ {order.paymentCompletedAt ? formatDate(order.paymentCompletedAt) : '-'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section: voided — admin notifies the customer (cancelled + money back on card). All admins. */}
      {voidNotifyOrders.length > 0 && (
        <div className="mt-8 border-t border-emerald-200 pt-4">
          <h3 className="text-lg font-bold text-emerald-700 mb-1">✅ Void แล้ว — รอแจ้งลูกค้า ({voidNotifyOrders.length})</h3>
          <p className="text-xs text-gray-500 mb-3">เงินคืนเข้าบัตรเรียบร้อย — แจ้งลูกค้าพร้อมแนบรูปยืนยัน แล้วกดปิดเคส</p>
          <div className="flex flex-col gap-2">
            {voidNotifyOrders.map((order) => (
              <div key={order.id} className="border border-emerald-200 bg-emerald-50 rounded p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{order.CustomerProfile?.contact || order.smileNameInput || '—'}</span>
                  <a href={order.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs">{order.url}</a>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  {order.cancelReceiptUrl && order.cancelReceiptUrl !== 'pending_upload' && (
                    <img src={order.cancelReceiptUrl} alt="void proof" onClick={() => setZoomSrc(order.cancelReceiptUrl)}
                      className="h-16 w-16 object-contain border border-gray-200 rounded bg-white cursor-pointer hover:opacity-80" />
                  )}
                  <button onClick={() => copyText(buildVoidNotice(order))}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium">คัดลอกข้อความแจ้ง ลค</button>
                  <button onClick={() => handleVoidResolve(order, 'customer_notified')}
                    className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-sm font-medium">✅ แจ้งลูกค้าแล้ว (ปิดเคส)</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  // ─── Main ─────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-3xl font-bold">โอนเงินคืนลูกค้า</h2>
        {activeTab !== 'report' && activeTab !== 'cancel' && (
          <button onClick={openModal}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium">
            + สร้างรายการใหม่
          </button>
        )}
      </div>

      <div className="flex mb-6 border-b flex-wrap">
        {STATUS_TABS.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 mr-1 font-medium text-base transition-colors flex items-center gap-1 ${
              activeTab === tab.id ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
            {tab.id !== 'report' && tab.id !== 'customer_notified' && tabCounts[tab.id] > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
              }`}>
                {tabCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'report' ? renderReport()
        : activeTab === 'cancel' ? renderCancelTab()
        : (
          <>
            {isLoading && <div className="text-center py-10 text-gray-500">กำลังโหลด...</div>}
            {!isLoading && filteredRecords.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-lg">ไม่มีรายการ</div>
            )}
            <div>{filteredRecords.map(renderCard)}</div>
          </>
        )}

      <RefundCreateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPendingCancelOrders(null);
          setCaseTicketPrefill(null);
          caseTicketIdRef.current = null;
        }}
        onCreated={handleCreated}
        prefill={caseTicketPrefill || (pendingCancelOrders && pendingCancelOrders.length ? {
          customerId: pendingCancelOrders[0].customerId,
          orderUrls: pendingCancelOrders.map((o) => o.url),
          // The real order ids, so same-URL orders don't collapse to one on lookup.
          orderIds: pendingCancelOrders.map((o) => o.id),
          contact: pendingCancelOrders[0].CustomerProfile?.contact,
          smileName: pendingCancelOrders[0].smileNameInput,
          // The order may already know why it ended — a ส่งตรวจ that came back
          // ไม่ผ่าน writes its reason when it routes the order here. Pre-select
          // that rather than make an admin re-state what the system recorded;
          // they can still change it. Only when every order agrees: a mixed
          // batch has no single reason to offer.
          reason: pendingCancelOrders.every((o) => o.cancelReason && o.cancelReason === pendingCancelOrders[0].cancelReason)
            ? pendingCancelOrders[0].cancelReason
            : undefined,
        } : null)}
        lockCustomer={caseTicketPrefill ? !!caseTicketPrefill.customerId : !!(pendingCancelOrders && pendingCancelOrders.length)}
      />

      <ImageUploadModal
        isOpen={!!qrModalRecordId}
        onRequestClose={() => setQrModalRecordId(null)}
        onConfirm={handleQrConfirm}
        title="แนบ QR พร้อมเพย์ (กด Ctrl+V เพื่อวางรูป)"
        maxFiles={1}
        acceptedFileTypes={['image/*']}
      />

      {zoomSrc && <ImageModal src={zoomSrc} onClose={() => setZoomSrc(null)} />}

      <ConfirmDialog
        open={!!confirmTakeover}
        title="เคสนี้มีผู้อื่นกำลังดำเนินการ"
        message={confirmTakeover?.message}
        confirmText="ดำเนินการต่อ"
        onConfirm={() => { const cb = confirmTakeover?.onConfirm; setConfirmTakeover(null); if (cb) cb(); }}
        onClose={() => setConfirmTakeover(null)}
      />

      {/* Receipt + notify modal (transferred tab). Open = claimed; close = released. */}
      {receiptRecord && (
        <div onClick={handleCloseReceipt}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <h3 className="text-lg font-bold">สลิปโอนเงิน — แจ้งลูกค้า</h3>
              <button onClick={handleCloseReceipt} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                <div className="text-sm"><span className="text-gray-500">Contact: </span><span className="font-semibold break-all">{receiptRecord.contact || '-'}</span></div>
                <button onClick={() => copyText(receiptRecord.contact)} className="shrink-0 px-2 py-1 rounded bg-blue-600 text-white text-xs">คัดลอก</button>
              </div>
              <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                <div className="text-sm"><span className="text-gray-500">Smile Name: </span><span className="font-semibold">{receiptRecord.smileName || '-'}</span></div>
                <button onClick={() => copyText(receiptRecord.smileName)} className="shrink-0 px-2 py-1 rounded bg-blue-600 text-white text-xs">คัดลอก</button>
              </div>
              <div className="text-sm text-gray-600">ยอดโอน: <span className="font-bold text-blue-900">฿{formatAmount(receiptRecord.amount)}</span></div>
            </div>

            {receiptRecord.slipUrl && (
              isPdf(receiptRecord.slipUrl) ? (
                <iframe src={receiptRecord.slipUrl} title="สลิป" className="w-full h-80 border border-gray-200 rounded" />
              ) : (
                <img src={receiptRecord.slipUrl} alt="สลิป" className="w-full rounded border border-gray-200 object-contain max-h-96" />
              )
            )}

            <div className="flex gap-2 mt-4">
              {receiptRecord.slipUrl && (
                <a href={receiptRecord.slipUrl} download target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-center py-2.5 rounded-lg bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium">⬇️ ดาวน์โหลดสลิป</a>
              )}
              <button onClick={handleNotifyFromModal}
                className="flex-1 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold">✅ แจ้งลูกค้าแล้ว</button>
            </div>
          </div>
        </div>
      )}

      {/* Inform-customer confirmation (no-refund cancellations) */}
      {informGroup && (
        <div onClick={() => setInformGroup(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-lg p-5">
            <h3 className="text-lg font-bold mb-1">แจ้งลูกค้า — ยกเลิก (ไม่คืนเงิน)</h3>
            <p className="text-xs text-gray-500 mb-3">ส่งข้อความนี้ให้ลูกค้าทาง LINE ก่อน แล้วกด “แจ้งแล้ว” เพื่อยืนยัน — ระบบจะบันทึกว่าใครเป็นผู้ยืนยัน</p>
            <textarea readOnly rows={7} value={buildCancelNotice(informGroup)}
              className="w-full border border-gray-300 rounded p-2 text-sm bg-gray-50" />
            <div className="flex justify-between items-center mt-3">
              <button
                onClick={() => navigator.clipboard.writeText(buildCancelNotice(informGroup)).then(() => toast.success('คัดลอกข้อความแล้ว')).catch(() => {})}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium">📋 คัดลอกข้อความ</button>
              <div className="flex gap-2">
                <button onClick={() => setInformGroup(null)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50">ยกเลิก</button>
                <button onClick={confirmInform}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium">แจ้งแล้ว</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation — soft-delete with a required reason for auditing. */}
      {deleteTarget && (
        <div onClick={() => !deleteSaving && setDeleteTarget(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-md p-5">
            <h3 className="text-lg font-bold mb-1 text-red-600">ลบรายการโอนเงินคืน</h3>
            <p className="text-sm text-gray-600 mb-3">
              {deleteTarget.contact || '-'}
              {deleteTarget.smileName && <span className="text-gray-400"> ({deleteTarget.smileName})</span>}
              {' — '}<span className="font-semibold text-blue-700">฿{formatAmount(deleteTarget.amount)}</span>
            </p>
            <p className="text-xs text-gray-500 mb-2">รายการจะถูกซ่อนแต่ยังเก็บไว้ในรายงาน (ดูได้ว่าใครลบ) — กรุณาระบุเหตุผล</p>
            <textarea rows={3} value={deleteReason} autoFocus
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="เหตุผลในการลบ เช่น สร้างซ้ำ / สร้างผิดคน"
              className="w-full border border-gray-300 rounded p-2 text-sm" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setDeleteTarget(null)} disabled={deleteSaving}
                className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50">ยกเลิก</button>
              <button onClick={handleConfirmDelete} disabled={deleteSaving || !deleteReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded font-medium">
                {deleteSaving ? 'กำลังลบ...' : 'ลบรายการ'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit amount — only before the money moves (backend blocks it after transfer). */}
      {amountTarget && (
        <div onClick={() => !amountSaving && setAmountTarget(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg w-full max-w-sm p-5">
            <h3 className="text-lg font-bold mb-1">แก้ไขยอดโอนคืน</h3>
            <p className="text-sm text-gray-600 mb-3">
              {amountTarget.contact || '-'}
              {amountTarget.smileName && <span className="text-gray-400"> ({amountTarget.smileName})</span>}
            </p>
            <label className="block text-xs text-gray-600 mb-1">ยอดใหม่ (บาท)</label>
            {/* text + inputMode so the value can't change on mouse-wheel scroll (type=number bug) */}
            <input type="text" inputMode="decimal" value={amountValue} autoFocus
              onChange={(e) => setAmountValue(e.target.value.replace(/[^\d.]/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleConfirmAmount()}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            <p className="text-xs text-gray-400 mt-2">หัวหน้าจะเห็นยอดใหม่นี้ที่หน้าโอนเงินทันที</p>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setAmountTarget(null)} disabled={amountSaving}
                className="px-4 py-2 border border-gray-300 rounded text-gray-600 hover:bg-gray-50 disabled:opacity-50">ยกเลิก</button>
              <button onClick={handleConfirmAmount} disabled={amountSaving || !(parseFloat(amountValue) > 0)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded font-medium">
                {amountSaving ? 'กำลังบันทึก...' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RefundTransfer;
